import React, { useContext, useEffect, useState } from 'react';
import { Button, Col, Modal, Row } from 'react-bootstrap';
import axios from 'axios';
import AuthContext from '../../../authentication/AuthContext';
import Moment from 'react-moment';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { faClock, faTrashAlt } from '@fortawesome/free-regular-svg-icons';
import UserContext from '../../../user/UserContext';
import { useAlert } from 'react-alert';
import { handlePresellError } from '../AlertUtils';
import ClearOrderModal from './ClearOrderModal';

export default function PresellHeader(props) {
    let cas = useContext(AuthContext);
    let { userDetails } = useContext(UserContext);

    let versionNumber = 0;

    const [name, setName] = useState('Loading...');
    const [orderTotal, setOrderTotal] = useState(0);
    const [savings, setSavings] = useState('Loading...');
    const [source, setSource] = useState('Loading...');
    const [status, setStatus] = useState(props.status ? props.status : 'Loading...');
    const [totalCases, setTotalCases] = useState('Loading...');
    const [firstDeliveryDate, setFirstDeliveryDate] = useState('0000-01-01');
    const [lastDeliveryDate, setLastDeliveryDate] = useState('0000-01-01');
    const [orderDeadline, setOrderDeadline] = useState('0000-01-02');
    const [errorMessages, setErrorMessages] = useState([]);
    const [deliveryWindows, setDeliveryWindows] = useState([]);
    const [disableSubmit, setDisableSubmit] = useState(props.disableSubmit);
    const [por, setPor] = useState(null);
    const [telesalesCancelOnly, setTelesalesCancelOnly] = useState(false);
    const [showClearConfirmation, setShowClearConfirmation] = useState(false);
    const [isAllocation, setIsAllocation] = useState(false);
    const [refreshOrderTotalsRequired, setRefreshOrderTotalsRequired] = useState(false);
    const alert = useAlert();
    const orderingClosed = moment().isAfter(orderDeadline);

    const scrollToDeal = dealId => {
        if (!dealId) {
            return;
        }

        props.setDealExpanded({ ...props.expandedDeals, [dealId]: true });

        let element = document.getElementById('presell-deal-' + dealId);
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = window.pageYOffset + elementPosition - 110; // 110 offset compensates for the mini header which is ~103 pixels high

        // 500ms delay allows deal to expand so that there is enough room to actually scroll
        setTimeout(() => window.scrollTo({ top: offsetPosition, behavior: 'smooth' }), 500);
    };

    function mapErrors() {
        return errorMessages.map(errorMessage => (
            <li
                key={Math.random().toString(36)}
                onClick={() => scrollToDeal(errorMessage.dealId)}
                data-testid={
                    'presell-header-deal-' +
                    (!!errorMessage.dealId ? errorMessage.dealId : 'global') +
                    '-error'
                }
                className={!!errorMessage.dealId ? 'presell-header-error-message' : ''}
            >
                {!!errorMessage.dealId && <strong>{errorMessage.dealName}: </strong>}
                {errorMessage.message}
            </li>
        ));
    }

    function mapDeliveryWindows() {
        return deliveryWindows.map(deliveryWindow => (
            <tr key={deliveryWindow.date}>
                <td className={'presell-delivery-window-date'}>
                    <Moment format={'dddd DD/MM/YYYY'}>{deliveryWindow.date}</Moment>
                </td>
                <td className={'presell-delivery-window-case-count'}>{deliveryWindow.cases}</td>
                <td className={'presell-delivery-window-cost'}>{formatCost(deliveryWindow.cost)}</td>
            </tr>
        ));
    }

    function updateSummary(summary) {
        setName(summary.name);
        setOrderTotal(summary.orderTotal);
        setSavings(summary.savings);
        setSource(summary.source);
        setStatus(summary.status);
        setTotalCases(summary.totalCases);
        setFirstDeliveryDate(summary.firstDeliveryDate);
        setLastDeliveryDate(summary.lastDeliveryDate);
        setErrorMessages(summary.errorMessageDTOS);
        setDeliveryWindows(summary.deliveryWindows);
        setPor(summary.por);
        setOrderDeadline(summary.orderDeadline);
    }

    function warnIfOrderNeedsResubmitting(lastStatus, currentStatus) {
        if (lastStatus === 'SUBMITTED' && currentStatus === 'OPEN') {
            alert.error('WARNING: The order will have to be submitted again.');
        }
    }

    function setAllocationFlags(state) {
        props.updateIsAllocation(state);
        setIsAllocation(state);
    }

    function PresellTimer(props) {
        const [time, setTime] = React.useState(null);
        const deadline = props.orderDeadline;

        const updateTime = () => {
            var today = moment();
            var endDate = moment(deadline, 'YYYY-MM-DD');

            var diffDays = endDate.diff(today, 'days');
            var diffHours = endDate.diff(today, 'hours') % 24;
            var diffMins = endDate.diff(today, 'minutes') % 60;
            setTime(diffDays + 'd ' + diffHours + 'h ' + diffMins + 'm');
        };

        React.useEffect(() => {
            updateTime();
            const intervalHandle = window.setInterval(updateTime, 1000);
            return () => window.clearInterval(intervalHandle);
        }, [deadline]);

        return !!time ? (
            <>
                <FontAwesomeIcon icon={faClock} /> {time} remaining
            </>
        ) : (
            <></>
        );
    }

    function refreshOrderTotals(state) {
        setRefreshOrderTotalsRequired(state);
    }

    const updateTotals = props.updateTotals;
    const orderId = props.orderId;
    const quantitiesUpdated = props.quantitiesUpdated;

    // need order status changed from scroll header
    useEffect(() => {
        if (userDetails && userDetails.currentFranchise && (updateTotals || refreshOrderTotalsRequired)) {
            getOrderSummary();
            quantitiesUpdated(false);
            refreshOrderTotals(false);
        }
    }, [cas, updateTotals, orderId, versionNumber, userDetails]);

    function getOrderSummary() {
        versionNumber += 1;
        let lastStatus = status;
        axios
            .get(
                window.CONFIG['ws.activoc-presell-public.orders.summary']
                    .replace('{orderId}', orderId)
                    .replace('{versionNumber}', versionNumber),
                {
                    headers: {
                        Authorization: cas,
                        'Content-Type': 'application/json',
                        'x-franchise-code': userDetails.currentFranchise.code
                    }
                }
            )
            .then(response => {
                if (response.data.versionNumber === versionNumber) {
                    const newStatus = response.data.status;
                    updateSummary(response.data);

                    setAllocationFlags(['ALLOCATION', 'REJECTED'].includes(newStatus));
                    warnIfOrderNeedsResubmitting(lastStatus, newStatus);
                    const today = moment();

                    const endDate = moment(response.data.orderDeadline, 'YYYY-MM-DD');
                    const orderingEnded = !today.isBefore(endDate);
                    if (orderingEnded) {
                        props.curtailOrderingTime();
                    }

                    const submitDisabled =
                        newStatus === 'SUBMITTED' ||
                        response.data.errorMessageDTOS.length > 0 ||
                        response.data.totalCases <= 0 ||
                        (orderingEnded && !userDetails.telesales);

                    setDisableSubmit(submitDisabled);

                    props.updateOtherHeaderSubmit(submitDisabled);
                    props.updatePresellHeaderThumbnail({ ...response.data, orderingClosed: orderingClosed });
                    setTelesalesCancelOnly(response.data.telesalesCancelOnly === 'Y');
                }
            })
            .catch(error => {
                handlePresellError(alert, error.response, 'Could not retrieve order summary.');
            });
    }

    function submitPresellOrder() {
        axios
            .post(
                window.CONFIG['ws.activoc-presell-public.orders.submit'].replace('{orderId}', orderId),
                {},
                {
                    headers: {
                        Authorization: cas,
                        'Content-Type': 'application/json',
                        'x-franchise-code': userDetails.currentFranchise.code
                    }
                }
            )
            .then(() => {
                alert.success(
                    'Presell order submitted successfully. Changes can still be made and resubmitted until the ordering deadline has passed.'
                );
                getOrderSummary();
            })
            .catch(error => {
                handlePresellError(alert, error, 'Could not submit Presell Order.');
            });
    }

    const orderStatusIsRejected = status === 'REJECTED';
    const showClearButton =
        !orderingClosed && (userDetails.telesales || (!telesalesCancelOnly && !orderStatusIsRejected));

    return (
        <>
            <div className={'presell-header-container'}>
                <h1>{name}</h1>
                <Row>
                    <Col sm={4}>
                        <p data-testid={'presell-header-order-deadline'}>
                            <strong>Presell Order Deadline: </strong>
                            <Moment format={'dddd DD/MM/YYYY'}>
                                {moment(orderDeadline).subtract(1, 'days')}
                            </Moment>
                        </p>
                        <p data-testid={'presell-header-order-status'}>
                            <strong>Status: </strong> {status}
                        </p>
                        <p data-testid={'presell-header-delivery-period'}>
                            <strong>Delivery Window: </strong>
                            <Moment format={'DD/MM/YYYY'}>{firstDeliveryDate}</Moment> to{' '}
                            <Moment format={'DD/MM/YYYY'}>{lastDeliveryDate}</Moment>
                        </p>
                        <p data-testid={'presell-header-last-edited'}>
                            <strong>Last Edited By: </strong> {source}
                        </p>
                    </Col>
                    <Col sm={{span: 4, offset: 4}} >
                        <table className={'presell-header-delivery-table mb-1'}>
                            <thead>
                                <tr>
                                    <th className={'presell-delivery-window-date'}>Delivery Window</th>
                                    <th className={'presell-delivery-window-case-count'}>Cases</th>
                                    <th className={'presell-delivery-window-cost'}>Cost</th>
                                </tr>
                            </thead>
                        </table>
                        <div
                            data-testid={'presell-header-delivery-table'}
                            className={deliveryWindows.length < 5 ? '' : 'headerTableScroll'}
                        >
                            <table className={'presell-header-delivery-table'}>
                                <tbody>{mapDeliveryWindows()}</tbody>
                            </table>
                        </div>
                        <table className={'mt-2'}>
                            <tbody>
                                <tr>
                                    <td data-testid={'presell-header-total-cases'} className={'cases'}>
                                        Total Cases: {totalCases}
                                    </td>
                                    {savings > 0 && (
                                        <td
                                            data-testid={'presell-header-total-savings'}
                                            className={'savings'}
                                        >
                                            Savings: {formatCost(savings)}
                                        </td>
                                    )}
                                </tr>
                            </tbody>
                        </table>
                    </Col>
                </Row>
                <Row className={'mt-3'}>
                    <Col sm={5}>
                        {!orderingClosed && (
                            <p className={'clock'}>
                                <span data-testid={'presell-header-countdown'} className={'countdown'}>
                                    <PresellTimer orderDeadline={orderDeadline} />
                                </span>
                            </p>
                        )}
                    </Col>
                    <Col sm={3}>
                        <div data-testid={'presell-header-total'} className={'orderTotal'}>
                            Total Cost: <span>{formatCost(orderTotal)}</span>
                        </div>
                    </Col>
                </Row>

                <Row>
                    <Col sm={5}>
                        <div className={'alignBottom'}>
                            {showClearButton && (
                                <Button
                                    variant={'secondary'}
                                    data-testid="clear-order-button"
                                    onClick={() => setShowClearConfirmation(true)}
                                >
                                    <FontAwesomeIcon icon={faTrashAlt} />
                                    {!isAllocation ? 'Clear' : orderStatusIsRejected ? 'Restore' : 'Reject'}
                                </Button>
                            )}
                            {telesalesCancelOnly && !userDetails.telesales && (
                                <div data-testid={'phone-telesales-message'}>
                                    <span className={'phone-telesales-message'}>
                                        To cancel this order please call telesales on 01904 488663 and select
                                        option 1.
                                    </span>
                                </div>
                            )}
                        </div>
                    </Col>
                    <Col sm={3}>
                        <div data-testid={'presell-header-por'} className={'orderPor'}>
                            {!!por ? 'Total Margin: ' + por + '%' : ''}
                        </div>
                    </Col>
                    <Col sm={4}>
                        <div className={'orderTotal'}>
                            {!props.isAllocation && (
                                <Button
                                    variant={'primary'}
                                    disabled={
                                        (!userDetails.telesales &&
                                            (!userDetails.currentFranchise ||
                                                userDetails.currentFranchise.onHold)) ||
                                        props.disableSubmit ||
                                        disableSubmit
                                    }
                                    data-testid={'submit-order-button'}
                                    onClick={submitPresellOrder}
                                >
                                    Submit
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </Button>
                            )}
                        </div>
                    </Col>
                </Row>
            </div>
            <Modal show={showClearConfirmation} size="lg" onHide={() => setShowClearConfirmation(false)}>
                <ClearOrderModal
                    setShowClearConfirmation={setShowClearConfirmation}
                    orderId={orderId}
                    refreshOrderQuantities={props.refreshOrderQuantities}
                    refreshOrderTotals={refreshOrderTotals}
                    franchiseCode={userDetails.currentFranchise.code}
                    collapseDeals={props.collapseDeals}
                    isAllocation={props.isAllocation}
                    orderStatus={status}
                />
            </Modal>
            {errorMessages.length > 0 ||
            (!userDetails.telesales &&
                (!userDetails.currentFranchise ||
                    userDetails.currentFranchise.onHold ||
                    props.displayQuantityRestrictionsMessage)) ? (
                <Row className={'presell-error-messages'}>
                    <div className={'icon'}>
                        <FontAwesomeIcon icon={faInfoCircle} />
                    </div>
                    <div className={'errorMessages'}>
                        {!userDetails.currentFranchise || userDetails.currentFranchise.onHold ? (
                            <div className={'text'}>
                                <h3>This account is currently on hold and is unable to place orders.</h3>
                                <ul>
                                    <li>
                                        To submit an order please create it here then contact telesales on
                                        01904 488663 and select option 1.
                                    </li>
                                </ul>
                            </div>
                        ) : (
                            ''
                        )}
                        {!userDetails.telesales && props.displayQuantityRestrictionsMessage ? (
                            <div className={'text'}>
                                <h3>
                                    There are quantity restrictions on some of the products in this presell.
                                </h3>
                                <ul>
                                    <li>
                                        To order outside of these restrictions contact telesales on 01904
                                        488663 and select option 1.
                                    </li>
                                </ul>
                            </div>
                        ) : (
                            ''
                        )}
                        {errorMessages.length > 0 ? (
                            <div className={'text'}>
                                <h3>There are errors with your order.</h3>
                                <ul>{mapErrors()}</ul>
                            </div>
                        ) : (
                            ''
                        )}
                    </div>
                </Row>
            ) : (
                ''
            )}
        </>
    );
}

function formatCost(input) {
    return (
        <strong>
            £{Math.trunc(input)}
            <sup>{(Math.abs(input) % 1).toFixed(2).substring(1)}</sup>
        </strong>
    );
}
